import React, { useState, useEffect } from "react"
import { colors } from "./../utils/colorMapper"
import { post } from "./../utils/authClient"
import { compareJson } from "./../utils/common"
import {
  PARENT_PROPERTIES,
  PARENT_PROPERTIES_REQUIRED_FIELDS,
  ORGANIZATION_TYPES,
} from "./../utils/constant"
import JsonObject from "./jsonObject"

const JsonView = ({
  jsonContent,
  setHoveredKey,
  hoveredKey,
  colorIndexes,
  setColorMapping,
  fileLoading,
  resetSelectedFileContent,
  selectedFile,
  selectedGroup,
  isFileLocked = false,
  userId,
  setHoveredPage,
}) => {
  const [editMode, setEditMode] = useState({})
  const [tempValue, setTempValue] = useState({})
  const [updatedJson, setUpdatedJson] = useState({})
  const [showPopup, setShowPopup] = useState(false)
  const [action, setAction] = useState(null) // To keep track of which button was clicked
  const [transferInProgress, setTransferInProgress] = useState(false)
  const [transferComplete, setTransferComplete] = useState(false)
  const [activeState, setActiveState] = useState("json_value")
  const [selectValue, setSelectValue] = useState("")
  const [inputValue, setInputValue] = useState("")
  const [selectedOrgType, setSelectedOrgType] = useState("")

  // Static array for the properties to be rendered
  const propertiesToRender = PARENT_PROPERTIES
  const hoveredColor = "rgba(253,255,50, 0.8)"

  useEffect(() => {
    setSelectedOrgType("")
  }, [selectedFile])

  useEffect(() => {
    if (hoveredKey) {
      const element = document.getElementById(hoveredKey)
      if (element) {
        const isCursorOnJsonView = document.querySelector(".json-view:hover")
        if (!isCursorOnJsonView) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center", // Scroll to the center of the element
          })
        }
      }
    }
  }, [hoveredKey])

  useEffect(() => {
    // Initialize updatedJson with jsonContent.result or an empty object
    if (jsonContent?.result) {
      //const updated = JSON.parse(JSON.stringify(jsonContent?.result))
      const updated = JSON.parse(JSON.stringify(jsonContent))

      if (
        jsonContent?.organization_type &&
        ORGANIZATION_TYPES.includes(jsonContent?.organization_type)
      ) {
        setSelectedOrgType(jsonContent?.organization_type)
      } else {
        updated.organization_type = ORGANIZATION_TYPES[0]
      }

      setUpdatedJson(updated || {})
    } else {
      setUpdatedJson({})
    }
  }, [jsonContent])

  useEffect(() => {
    setColorMapping(colorIndexes) // Only set color mapping once when JSON is updated
  })

  const handleToggle = (action) => {
    setActiveState((prev) =>
      prev === "json_object" ? "json_value" : "json_object"
    )
  }

  const handleEditClick = (key, value, index, category) => {
    setEditMode({ ...editMode, [key]: true })
    setTempValue({ ...tempValue, [key]: value.data || "" })
  }

  const handleCancelClick = (key) => {
    setEditMode({ ...editMode, [key]: false })
    setTempValue((prev) => ({
      ...prev,
      [key]: updatedJson[key]?.data || "",
    }))
  }

  const handleSaveClick = (tmpKey, category, index, key) => {
    const newJson = { ...updatedJson } // Create a copy of the current updatedJson

    // Check if the category exists in updatedJson
    if (newJson["result"][category]) {
      // Check if the category is an array and handle accordingly
      if (Array.isArray(newJson["result"][category])) {
        // Check if the index is valid
        if (newJson["result"][category][index]) {
          // Check if the key exists and initialize if necessary
          if (!newJson["result"][category][index][key]) {
            newJson["result"][category][index][key] = {
              data: "",
              confidence_score: null,
            } // Initialize if undefined
          }

          // Update the specific value
          newJson["result"][category][index][key].data = tempValue[tmpKey]
        } else {
          console.error(
            `Index ${index} is out of bounds for category ${category}.`
          )
        }
      } else if (
        typeof newJson["result"][category] === "object" &&
        newJson["result"][category] !== null
      ) {
        // If the category is a single object (not an array)
        if (!newJson["result"][category][key]) {
          newJson["result"][category][key] = {
            data: "",
            confidence_score: null,
          } // Initialize if undefined
        }

        // Update the specific value
        newJson["result"][category][key].data = tempValue[tmpKey]
      } else {
        console.error(`Category ${category} is neither an array nor an object.`)
      }

      // Update the state with the new JSON
      setUpdatedJson(newJson)
      setEditMode({ ...editMode, [tmpKey]: false }) // Disable edit mode after saving
    } else {
      console.error(`Category ${category} not found in updatedJson.`)
    }
  }

  const handleDeleteClick = (category, index, key) => {
    const newJson = { ...updatedJson } // Create a copy of the current updatedJson

    // Check if the category exists in updatedJson
    if (newJson["result"][category]) {
      // Check if the category is an array and handle accordingly
      if (Array.isArray(newJson["result"][category])) {
        // Check if the index is valid
        if (newJson["result"][category][index]) {
          // Check if the key exists before attempting to delete
          if (newJson["result"][category][index][key]) {
            //delete newJson["result"][category][index][key] // Remove the key-value pair
            newJson["result"][category][index][key] = "OBJECT_REMOVED"

            // If the object at index becomes empty, remove the entire entry
            if (Object.keys(newJson["result"][category][index]).length === 0) {
              newJson["result"][category].splice(index, 1)
            }
          } else {
            console.error(
              `Key ${key} not found in category ${category} at index ${index}.`
            )
          }
        } else {
          console.error(
            `Index ${index} is out of bounds for category ${category}.`
          )
        }
      } else if (
        typeof newJson["result"][category] === "object" &&
        newJson["result"][category] !== null
      ) {
        // If the category is a single object (not an array)
        if (newJson["result"][category][key]) {
          //delete newJson["result"][category][key] // Remove the key-value pair
          newJson["result"][category][key] = "OBJECT_REMOVED"

          // If the object becomes empty, remove the entire category
          // if (Object.keys(newJson["result"][category]).length === 0) {
          //   delete newJson["result"][category]
          // }
        } else {
          console.error(`Key ${key} not found in category ${category}.`)
        }
      } else {
        console.error(`Category ${category} is neither an array nor an object.`)
      }

      // Update the state with the new JSON
      setUpdatedJson(newJson)
    } else {
      console.error(`Category ${category} not found in updatedJson.`)
    }
  }

  const clearFieldAddValues = () => {
    setSelectValue("")
    setInputValue("")
  }

  // const handleAddSubmitClick = (property, type) => {
  //   const newJson = { ...updatedJson } // Clone the current JSON
  //   const requiredFields = PARENT_PROPERTIES_REQUIRED_FIELDS[property]

  //   if (!newJson["result"][property]) {
  //     console.error(`Category ${property} not found in updatedJson.`)
  //     return
  //   }

  //   if (Array.isArray(newJson["result"][property])) {
  //     let missingKeyFound = false

  //     if (newJson["result"][property].length === 0) {
  //       // Add new object in case of [] Array
  //       const newObjectToAdd = {}
  //       requiredFields.forEach((field) => {
  //         newObjectToAdd[field] = { data: "", bounding_box: null }
  //       })
  //       newJson["result"][property].push(newObjectToAdd)
  //       setUpdatedJson(newJson)
  //       return
  //     } else {
  //       // Add missing field from array
  //       newJson["result"][property].forEach((item) => {
  //         // Check if any required field is missing in the current object
  //         requiredFields.forEach((field) => {
  //           if (!(field in item)) {
  //             item[field] = { data: "", bounding_box: null }
  //             missingKeyFound = true
  //           }
  //         })
  //       })

  //       if (missingKeyFound) {
  //         setUpdatedJson(newJson)
  //         return
  //       }
  //     }

  //     // If all required fields are present in each object, create a new one
  //     const lastObject = newJson["result"][property].slice(-1)[0]

  //     // Check if all required fields are present in the last object
  //     const allKeysPresent = requiredFields.every(
  //       (field) => field in lastObject
  //     )

  //     if (allKeysPresent) {
  //       const newObject = {}
  //       requiredFields.forEach((field) => {
  //         newObject[field] = { data: "", bounding_box: null } // Initialize with default values
  //       })
  //       newJson["result"][property].push(newObject) // Add new object
  //     }

  //     // Update the field specified by selectValue (if needed)
  //     if (lastObject[selectValue]) {
  //       lastObject[selectValue].data = inputValue
  //     }
  //   } else if (
  //     typeof newJson["result"][property] === "object" &&
  //     newJson["result"][property] !== null
  //   ) {
  //     // Handle the object case
  //     const categoryObject = newJson["result"][property]

  //     requiredFields.forEach((field) => {
  //       if (!(field in categoryObject)) {
  //         categoryObject[field] = { data: "", bounding_box: null } // Initialize missing fields
  //       }
  //     })
  //   } else {
  //     console.error(`Category ${property} is neither an array nor an object.`)
  //   }

  //   setUpdatedJson(newJson)
  //   clearFieldAddValues()
  // }

  const handleAddSubmitClick = (property, type) => {
    const newJson = { ...updatedJson } // Clone the current JSON
    const requiredFields = PARENT_PROPERTIES_REQUIRED_FIELDS[property]

    if (!newJson["result"][property]) {
      console.error(`Category ${property} not found in updatedJson.`)
      return
    }

    if (Array.isArray(newJson["result"][property])) {
      let missingKeyFound = false

      if (newJson["result"][property].length === 0) {
        // Add new object if array is empty
        const newObjectToAdd = {}
        requiredFields.forEach((field) => {
          newObjectToAdd[field] = { data: "", bounding_box: null }
        })
        newJson["result"][property].push(newObjectToAdd)
        setUpdatedJson(newJson)
        return
      } else {
        // Restore or add missing fields in existing array objects
        newJson["result"][property].forEach((item) => {
          requiredFields.forEach((field) => {
            if (!(field in item) || item[field] === "OBJECT_REMOVED") {
              item[field] = { data: "", bounding_box: null }
              missingKeyFound = true
            }
          })
        })

        if (missingKeyFound) {
          setUpdatedJson(newJson)
          return
        }
      }

      // If all required fields are already present in the last object, create a new object
      const lastObject = newJson["result"][property].slice(-1)[0]
      const allKeysPresent = requiredFields.every(
        (field) => field in lastObject && lastObject[field] !== "OBJECT_REMOVED"
      )

      if (allKeysPresent) {
        const newObject = {}
        requiredFields.forEach((field) => {
          newObject[field] = { data: "", bounding_box: null }
        })
        newJson["result"][property].push(newObject)
      }

      // Optional field update (based on selectValue/inputValue)
      const last = newJson["result"][property].slice(-1)[0]
      if (last[selectValue]) {
        last[selectValue].data = inputValue
      }
    } else if (
      typeof newJson["result"][property] === "object" &&
      newJson["result"][property] !== null
    ) {
      // Handle the object case (e.g., Member)
      const categoryObject = newJson["result"][property]

      requiredFields.forEach((field) => {
        if (
          !(field in categoryObject) ||
          categoryObject[field] === "OBJECT_REMOVED"
        ) {
          categoryObject[field] = { data: "", bounding_box: null }
        }
      })
    } else {
      console.error(`Category ${property} is neither an array nor an object.`)
    }

    setUpdatedJson(newJson)
    clearFieldAddValues()
  }

  // const renderConfidenceScore = (confidence_score, colorIndex, fieldKeyI) => {
  //   const percentage = Math.round(confidence_score * 100)
  //   const barColor = colors[colorIndex] // Get color based on confidence score
  //   colorIndexes[fieldKeyI] = colors[colorIndex] // Update local color mapping state

  //   return (
  //     <div className="confidence-score-div">
  //       <div className="confidence-score-bar">
  //         <div
  //           style={{
  //             width: `${percentage}%`,
  //             backgroundColor: barColor,
  //             height: "100%",
  //           }}
  //         />
  //       </div>
  //       {/* <strong>{percentage}%</strong> */}
  //     </div>
  //   )
  // }

  const getLengthOfPropertyWithObjectRemoved = (property) => {
    return Object.keys(property).filter((key) => {
      const val = property[key]
      if (typeof val === "string") {
        return val !== "OBJECT_REMOVED"
      } else if (val && typeof val === "object") {
        return val.data !== "OBJECT_REMOVED"
      }
      return false
    })
  }

  const renderField = (
    key,
    value,
    category,
    colorIndex,
    index,
    i,
    isNewArrObj = false
  ) => {
    if (
      !PARENT_PROPERTIES_REQUIRED_FIELDS[category].includes(key) ||
      value === "OBJECT_REMOVED"
    ) {
      return <></>
    }

    // if (Object.keys(value).length === 0) {
    //   return
    // }

    // Fix for same bounding for two fields Ex: Firstname and Lastname
    const fieldsForSkipColorIndex = ["Name_Last"]
    if (fieldsForSkipColorIndex.includes(key)) {
      colorIndex = colorIndex - 1
    }

    const displayValue = value?.data !== undefined ? value.data : value // Adjust to handle nested data

    // Check if the displayValue is an object and convert it to string if necessary
    const valueToDisplay =
      typeof displayValue === "object" && displayValue !== null
        ? JSON.stringify(displayValue) // Convert object to string for rendering
        : displayValue || "N/A" // Fallback to N/A

    // const confidence = value?.confidence_score
    //   ? value?.confidence_score
    //   : value?.confidence

    const fieldKeyI =
      i || i === 0 ? `${category}:${key}:${i}` : `${category}:${key}`

    const barColor = colors[colorIndex]
    colorIndexes[fieldKeyI] = colors[colorIndex]

    return (
      <>
        {/* {PARENT_PROPERTIES_REQUIRED_FIELDS[category]?.length > 1 &&
          i % PARENT_PROPERTIES_REQUIRED_FIELDS[category]?.length === 0 &&
          i !== 0 && <div className="divider"></div>} */}

        {isNewArrObj && <div className="divider"></div>}

        <div
          id={fieldKeyI}
          key={fieldKeyI}
          className="json-item"
          onMouseEnter={() => {
            if (value?.bounding_box?.top_left_x) {
              setHoveredKey(fieldKeyI)
              setHoveredPage(value?.page_number)
            }
          }}
          onMouseLeave={() => setHoveredKey(null)}
          style={{
            border: value?.bounding_box?.top_left_x
              ? `2px solid ${barColor}`
              : "1px dashed #ccc",
            backgroundColor:
              hoveredKey === fieldKeyI ? hoveredColor : "#f9f9f9",
          }}
        >
          {editMode[fieldKeyI] ? (
            <span>
              <strong>{key}: </strong>
              <span>
                <input
                  className="editInput"
                  type="text"
                  value={tempValue[fieldKeyI] || ""}
                  onChange={(e) =>
                    setTempValue({
                      ...tempValue,
                      [fieldKeyI]: e.target.value,
                    })
                  }
                />
                <button
                  className="save-button"
                  onClick={() =>
                    handleSaveClick(fieldKeyI, category, index, key)
                  }
                >
                  ✓
                </button>
                <button
                  className="cancel-button"
                  onClick={() => handleCancelClick(fieldKeyI)}
                >
                  ✗
                </button>
              </span>
            </span>
          ) : (
            <span
              onClick={() => handleEditClick(fieldKeyI, value, index, category)}
              style={{
                cursor: "pointer",
                display: "inline-block",
                position: "relative",
              }}
            >
              <strong>{key}: </strong>
              {valueToDisplay}
              <span className="edit-icon">✏️</span>
            </span>
          )}
          {/* {(value?.confidence_score || value?.confidence) &&
            renderConfidenceScore(confidence, colorIndex, fieldKeyI)} */}

          {/* {value?.bounding_box === null && ( */}
          <span
            onClick={() => handleDeleteClick(category, index, key)}
            style={{ fontSize: "10px" }}
          >
            ❌
          </span>
          {/* )} */}
        </div>
      </>
    )
  }

  const renderBox = (property, updatedJsonResult) => {
    if (!PARENT_PROPERTIES_REQUIRED_FIELDS[property]) {
      return <></>
    }

    return (
      <>
        <div
          key={`${property}-container`}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginTop: `${
              Object.keys(updatedJsonResult[property])?.length === 0
                ? "10px"
                : "10px"
            }`,
          }}
        >
          <h3 key={`${property}-heading`} style={{ marginLeft: "3px" }}>
            {property}
            <span style={{ fontSize: "12px", fontWeight: "normal" }}>{`${
              Array.isArray(updatedJsonResult[property])
                ? " [array]"
                : " {object}"
            }`}</span>
          </h3>

          {(PARENT_PROPERTIES_REQUIRED_FIELDS[property]?.length !==
            getLengthOfPropertyWithObjectRemoved(updatedJsonResult[property])
              .length ||
            Array.isArray(updatedJsonResult[property])) && (
            <span
              style={{ color: "blue", cursor: "pointer", marginRight: "8px" }}
              onClick={() => handleAddSubmitClick(property)}
            >
              ➕
            </span>
          )}
        </div>

        {Object.keys(updatedJsonResult[property])?.length === 0 && (
          <div
            className="json-item"
            style={{
              backgroundColor: "#f9f9f9",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              color: "grey",
            }}
          >
            N/A
          </div>
        )}
      </>
    )
  }

  const renderJsonData = () => {
    const updatedJsonResult = updatedJson?.result

    if (
      !updatedJsonResult ||
      Object.keys(updatedJsonResult).length === 0 ||
      isFileLocked
    ) {
      return (
        <div className="display-center-p">
          {/* <p>No JSON data available</p> */}
        </div>
      )
    }

    const jsonDataArray = []
    let colorIndex = 0 // Reset colorIndex for each render

    // Errors
    jsonDataArray.push(
      <>
        {Array.isArray(jsonContent?.errors) &&
          jsonContent?.errors.length > 0 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginTop: "0px",
                  cursor: "default",
                }}
              >
                <h3
                  style={{
                    marginLeft: "3px",
                    color: "red",
                  }}
                >
                  Errors
                </h3>
              </div>
              {jsonContent.errors.map((error, k) => {
                if (
                  !error ||
                  typeof error !== "object" ||
                  Object.keys(error).length === 0
                ) {
                  return null // Skip invalid error objects
                }

                return (
                  <div
                    key={k} // Ensure a unique key for each item
                    className="json-item" // Use className instead of class
                    style={{
                      border: "1px dashed #ccc",
                      backgroundColor: "#f9f9f9",
                      cursor: "default",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        position: "relative",
                      }}
                    >
                      <strong>{Object.keys(error)[0]}: </strong>
                      {Object.values(error)[0]}
                    </span>
                  </div>
                )
              })}
            </>
          )}
      </>
    )

    propertiesToRender.forEach((property) => {
      if (Array.isArray(updatedJsonResult[property])) {
        jsonDataArray.push(renderBox(property, updatedJsonResult))

        let i = 0
        updatedJsonResult[property].forEach((item, index) => {
          let fieldCount = 0

          // SORT as per constant
          const sortedItem = Object.fromEntries(
            [
              ...PARENT_PROPERTIES_REQUIRED_FIELDS[property],
              ...Object.keys(item).filter(
                (key) =>
                  !PARENT_PROPERTIES_REQUIRED_FIELDS[property].includes(key)
              ),
            ]
              .filter((key) => key in item)
              .map((key) => [key, item[key]])
          )
          // SORT as per constant

          Object.entries(sortedItem).forEach(([key, value]) => {
            if (PARENT_PROPERTIES_REQUIRED_FIELDS[property].includes(key)) {
              fieldCount++
            }

            jsonDataArray.push(
              renderField(
                key,
                value,
                property,
                colorIndex,
                index,
                i,
                fieldCount === 1 && index !== 0 ? true : false
              )
            )
            colorIndex++
            i++
          })
        })
      } else if (
        typeof updatedJsonResult[property] === "object" &&
        updatedJsonResult[property] !== null
      ) {
        jsonDataArray.push(renderBox(property, updatedJsonResult))

        // Access nested properties for Member, Requesting_Provider, and Ordering_Provider
        Object.entries(updatedJsonResult[property]).forEach(([key, value]) => {
          jsonDataArray.push(
            renderField(key, value, property, colorIndex, null)
          )
          colorIndex++
        })
      } else {
        jsonDataArray.push(
          <div key={`${property}-no-data`}>
            <strong>{property}:</strong>{" "}
            {JSON.stringify(updatedJsonResult[property])}
          </div>
        )
      }
    })

    return jsonDataArray
  }

  const renderJsonObject = () => {
    return (
      <JsonObject
        jsonData={updatedJson}
        onUpdate={(updatedJson) => setUpdatedJson(updatedJson)}
      />
    )
  }

  const handleButtonClick = (selectedAction) => {
    setAction(selectedAction)
    setShowPopup(true)
  }

  const handleConfirm = async () => {
    try {
      setTransferInProgress(true)

      const changes = compareJson(jsonContent, updatedJson)

      // console.log("->  updatedJson:", updatedJson)
      // console.log("->  jsonContent:", jsonContent)
      // console.log("->  changes:", changes)
      // throw new Error("Debug")

      // Convert finalJson to a Base64-encoded string
      const finalJsonString = JSON.stringify(updatedJson)
      const finalJsonBase64 = btoa(
        unescape(encodeURIComponent(finalJsonString))
      )

      const params = {
        jsonContent: finalJsonBase64,
        fileName: selectedFile,
        groupName: selectedGroup,
        userId,
        changes: JSON.stringify(changes),
      }

      if (action === "Update") {
        await post("/clarity/process/storage", params)
      } else if (action === "Send") {
        await post("/clarity/process/complete", params)
      }

      // If the API call succeeds, proceed
      setTransferInProgress(false)
      setTransferComplete(true)
      resetSelectedFileContent()
      setUpdatedJson({})
    } catch (error) {
      // Handle API errors here
      setTransferInProgress(false)
      setShowPopup(false)
      console.error("Error during transfer:", error)
    }
  }

  const handleClosePopup = () => {
    setShowPopup(false)
    setTransferComplete(false)
    setAction(null)
    //setSelectedOrgType(jsonContent?.organization_type)
  }

  if (fileLoading) {
    return <div></div>
  }

  return (
    <div className="json-view">
      {(!updatedJson || Object.keys(updatedJson).length === 0) === false &&
        !isFileLocked && (
          <div className="json-view-buttons">
            <button className="red" onClick={() => handleButtonClick("Update")}>
              Manual Review
            </button>
            <h3
              className="json-view-object-toggle"
              onClick={() => handleToggle("json_object")}
            >
              JSON {activeState === "json_object" ? "{ }" : "[=]"}
            </h3>
            <button className="green" onClick={() => handleButtonClick("Send")}>
              Send to Jiva
            </button>
          </div>
        )}

      <div className="json-container">
        {activeState === "json_value" && selectedFile && !isFileLocked && (
          <div className="json-item" style={{ marginTop: "0px" }}>
            <span>
              <div>
                <label style={{ color: "#007bff", fontWeight: "bold" }}>
                  {" "}
                  Organization Type:{" "}
                </label>
                <select
                  id="organizationType"
                  className="org-type-select"
                  value={selectedOrgType}
                  onChange={(e) => {
                    setSelectedOrgType(e.target.value)
                    const tmpJson = { ...updatedJson }
                    tmpJson.organization_type = e.target.value
                    setUpdatedJson(tmpJson)
                  }}
                >
                  {ORGANIZATION_TYPES.map((org, k) => (
                    <option key={k} value={org}>
                      {org}
                    </option>
                  ))}
                </select>
              </div>
            </span>
          </div>
        )}

        {selectedFile &&
          (activeState === "json_value"
            ? renderJsonData()
            : renderJsonObject())}

        {showPopup && (
          <div className="popup-overlay">
            <div className="popup">
              {transferComplete ? (
                <div className="success-message">
                  <span
                    style={{
                      color: "green",
                      fontSize: "48px",
                    }}
                  >
                    ✅
                  </span>
                  <p>{`File successfully sent ${
                    action === "Update" ? "for Manual Review" : "to Jiva"
                  }`}</p>
                  <button onClick={handleClosePopup}>Close</button>
                </div>
              ) : transferInProgress ? (
                <div className="transfer-animation">
                  <p>
                    {`Transferring file ${
                      action === "Update" ? "for Manual Review" : "to Jiva"
                    }`}
                  </p>
                  <div className="file-transfer-animation"></div>
                </div>
              ) : (
                <div className="confirmation">
                  <p>
                    {`Are you sure you want to send this document ${
                      action === "Update" ? "for Manual Review" : "to Jiva"
                    }?`}
                  </p>
                  <button onClick={handleConfirm}>Yes</button>
                  <button onClick={handleClosePopup}>No</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default JsonView
